import type { AxiosInstance } from 'axios';
import { createHttp, http } from '@/http/axios';
import type { AnalyticsEventInput } from '@/features/live/types';
import {
  ANALYTICS_RETRY_DELAYS_MS,
  createAnalyticsEventId,
  isRetryableAnalyticsError,
} from '@/lib/analyticsIngestion';
import {
  browserCanaryStorage,
  isLiveApiEligibleAuthorization,
  parseCanaryPercent,
  readCanaryBucket,
  shouldUseLiveApi,
} from '@/lib/liveApiRouting';

const LIVE_API_URL = import.meta.env.VITE_LIVE_API_URL as string | undefined;
/** Canary distinct de celui des lectures : à 0 (ou absent), tout reste sur welya-api. */
const ANALYTICS_CANARY_PERCENT = parseCanaryPercent(
  import.meta.env.VITE_LIVE_API_ANALYTICS_CANARY_PERCENT as string | undefined,
);
const LIVE_API_TIMEOUT_MS = 5000;

let liveApiHttp: AxiosInstance | null | undefined;

function resolveLiveApiHttp(): AxiosInstance | null {
  if (liveApiHttp === undefined) {
    const enabled = shouldUseLiveApi(LIVE_API_URL, ANALYTICS_CANARY_PERCENT, () =>
      readCanaryBucket(browserCanaryStorage()),
    );
    // un refus du token spectateur est définitif : le refresh de session ne le change pas
    liveApiHttp =
      enabled && LIVE_API_URL
        ? createHttp(LIVE_API_URL, LIVE_API_TIMEOUT_MS, { recoverSession: false })
        : null;
  }
  return liveApiHttp;
}

function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/**
 * Le pipeline est choisi une fois par événement et n'en change jamais : passer de live-api à welya-api
 * créerait un second événement. Côté live-api, les retries gardent le même eventId et le consumer
 * déduplique. welya-api ne déduplique pas à l'entrée : il reçoit un seul envoi, comme avant.
 */
export async function postAnalyticsEvent(
  webinarId: number,
  sessionIdentifier: string,
  authorization: string,
  input: AnalyticsEventInput,
): Promise<void> {
  const path = `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/analytics`;
  const body = { ...input, eventId: createAnalyticsEventId() };
  const config = { headers: { Authorization: authorization } };
  const liveApi = isLiveApiEligibleAuthorization(authorization) ? resolveLiveApiHttp() : null;

  if (!liveApi) {
    await http.post(path, body, config);
    return;
  }

  for (let attempt = 0; ; attempt++) {
    try {
      await liveApi.post(path, body, config);
      return;
    } catch (error) {
      const retryDelayMs = ANALYTICS_RETRY_DELAYS_MS[attempt];
      if (retryDelayMs === undefined || !isRetryableAnalyticsError(error)) throw error;
      await wait(retryDelayMs);
    }
  }
}
